import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Countdown } from "@app/components/Countdown.tsx";
import { useGame } from "@app/state/game.ts";
import { api } from "@app/api/client.ts";
import { track } from "@app/analytics.ts";
import { BAND_UI } from "@app/format.ts";
import { MAX_SCORE_PER_ROUND, buildShareText, resultUrl, whatsappUrl, xUrl, type ShareChannel } from "@shared/share";
import type { Band, PlayerStats } from "@shared/types";

export function Result() {
  const navigate = useNavigate();
  const round = useGame((s) => s.round);
  const guesses = useGame((s) => s.guesses);
  const totalScore = useGame((s) => s.totalScore);
  const percentile = useGame((s) => s.percentile);
  const currentStreak = useGame((s) => s.currentStreak);
  const shareId = useGame((s) => s.shareId);
  const completed = useGame((s) => s.completed);
  const loading = useGame((s) => s.loading);
  const load = useGame((s) => s.load);
  const resetLocal = useGame((s) => s.resetLocal);
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!loading && !completed) navigate("/");
  }, [loading, completed, navigate]);

  useEffect(() => {
    void api
      .stats()
      .then(setStats)
      .catch(() => setStats(null));
  }, []);

  if (!round || totalScore === null) {
    return (
      <section className="screen">
        <p className="status">Cargando tu resultado…</p>
      </section>
    );
  }

  const bands: Band[] = guesses.map((g) => g.band);
  const shareText = shareId
    ? buildShareText({
        roundNumber: round.number,
        bands,
        total: totalScore,
        url: resultUrl(window.location.origin, shareId),
      })
    : "";

  const record = (channel: ShareChannel) =>
    track({ name: "share_click", roundId: round.id, channel, score: totalScore });

  const share = async () => {
    if (!shareId) return;
    if (navigator.share) {
      record("native");
      try {
        await navigator.share({ text: shareText });
      } catch {
        /* el usuario canceló */
      }
      return;
    }
    await copy();
  };

  const copy = async () => {
    record("copy");
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const newRound = () => {
    resetLocal();
    void load();
    navigate("/");
  };

  return (
    <section className="screen" aria-labelledby="resTitle">
      <div className="results">
        <div className="scorebox">
          <h2 className="scorebox__k" id="resTitle">
            Ronda #{round.number} · Tu resultado
          </h2>
          <div className="big">
            {totalScore}
            <small>/{MAX_SCORE_PER_ROUND}</small>
          </div>
          <div className="chips">
            {guesses.map((g, i) => (
              <div
                key={g.position}
                className={`chip chip--${BAND_UI[g.band].cls}`}
                aria-label={`Pista ${i + 1}: ${g.score} puntos`}
              >
                <i aria-hidden="true">{BAND_UI[g.band].icon}</i>
                {g.score}
              </div>
            ))}
          </div>
          <p className="pct">
            {percentile === null ? (
              <>Aún no hay suficientes jugadores de hoy para calcular tu percentil</>
            ) : (
              <>
                Mejor que el <b>{percentile}%</b> de jugadores de hoy
              </>
            )}
          </p>
        </div>
        <div className="stats">
          <div className="stat">
            <b>{currentStreak ?? 0}</b>
            <span>Racha</span>
          </div>
          <div className="stat">
            <b>{stats?.played ?? "—"}</b>
            <span>Jugadas</span>
          </div>
          <div className="stat">
            <b>
              <Countdown targetIso={round.nextRolloverAt} onZero={newRound} />
            </b>
            <span>Próxima</span>
          </div>
        </div>
        <button type="button" className="cta" onClick={() => void share()}>
          Compartir resultado
        </button>
        <div className="share-row">
          <button
            type="button"
            className="cta cta--ghost"
            onClick={() => {
              record("whatsapp");
              window.open(whatsappUrl(shareText), "_blank", "noopener");
            }}
          >
            WhatsApp
          </button>
          <button type="button" className="cta cta--ghost" onClick={() => void copy()}>
            {copied ? "¡Copiado!" : "Copiar"}
          </button>
          <button
            type="button"
            className="cta cta--ghost"
            onClick={() => {
              record("x");
              window.open(xUrl(shareText, shareId ? resultUrl(window.location.origin, shareId) : ""), "_blank", "noopener");
            }}
          >
            X
          </button>
        </div>
        <pre className="share-pre">{shareText}</pre>
        <button type="button" className="cta cta--ghost" onClick={() => navigate("/")}>
          Volver al inicio
        </button>
      </div>
    </section>
  );
}
